import { useObserva } from '@morganisbatman/observa-client-react'
import { useEffect, useRef } from 'react'
import { Platform } from 'react-native'

// Per-screen lifecycle telemetry. The app routes by state (not URL), so the
// SDK's history-based page-view capture can't see screen changes — this fills
// that gap. Two emits, both untyped via the raw client (the app has no frontend
// taxonomy; server events are the typed ones):
//
//   screen.viewed { screen, from }       — on enter; the in-app analogue of
//                                          page.viewed. `from` is the previous
//                                          screen, so the sequence reconstructs
//                                          which pages users navigate amongst.
//   screen.dwell  { screen, durationMs } — on leave; foreground time on it.
//
// screen.viewed fires on every platform. screen.dwell is web-only (native
// screen-time isn't wired). Dwell counts FOREGROUND time only: hiding the tab
// flushes the elapsed slice and the clock restarts on return, so a screen left
// open in a background tab doesn't inflate the time.
export const useScreenTracking = (screen: string) => {
  const observa = useObserva()
  const from = useRef<string | null>(null)

  useEffect(() => {
    void observa
      .event({ eventType: 'screen.viewed', payload: { screen, from: from.current } })
      .catch(() => {})
    from.current = screen

    if (Platform.OS !== 'web' || typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }
    const now = () =>
      typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
    let startedAt = now()
    const flush = () => {
      const durationMs = Math.round(now() - startedAt)
      startedAt = now()
      if (durationMs <= 0) return
      void observa
        .event({ eventType: 'screen.dwell', payload: { screen, durationMs } })
        .catch(() => {})
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush()
      else startedAt = now()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onVisibility)
      flush()
    }
  }, [screen, observa])
}
